// v2
// Path: frontend/src/components/SyncHub/shared/MappingDesignerTable.jsx
// Inputs: isStandardMode, searchFilter, setSearchFilter, selectedQBEntity, qbSampleRecords, activeQbSampleIndex, setActiveQbSampleIndex, pbsSampleRecords, activeSampleIndex, setActiveSampleIndex, activeTableName, filteredMappings, definitions, sourceColumns, newTarget, setNewTarget, newSource, setNewSource, handleAddCustomMapping, handleCustomMappingChange, handleRemoveCustomRow
// Outputs: 3-column Left-to-Right ETL mapping table (Source Builder -> Transform & Operator -> Target Destination) with interactive token chips, searchable column popovers, and custom endpoint creation.
// Summary: Responsive Left-to-Right ETL mapping canvas enforcing natural data flow from ERP source inputs through transformation/delimiter operators into QuickBooks destination schemas.

import React, { useState, useRef, useEffect } from 'react';

export default function MappingDesignerTable(props) {
    const {
        isStandardMode,
        searchFilter,
        setSearchFilter,
        selectedQBEntity,
        qbSampleRecords,
        activeQbSampleIndex,
        setActiveQbSampleIndex,
        pbsSampleRecords,
        activeSampleIndex,
        setActiveSampleIndex,
        activeTableName,
        filteredMappings,
        definitions,
        newTarget,
        setNewTarget,
        newSource,
        setNewSource,
        handleAddCustomMapping,
        handleCustomMappingChange,
        handleRemoveCustomRow
    } = props;

    // State for "+ Add Field" popover
    const [activePopoverTarget, setActivePopoverTarget] = useState(null);
    const [popoverSearch, setPopoverSearch] = useState('');
    const popoverRef = useRef(null);

    // Light dismiss for column popover (click outside or Esc)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setActivePopoverTarget(null);
                setPopoverSearch('');
            }
        };
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setActivePopoverTarget(null);
                setPopoverSearch('');
            }
        };

        if (activePopoverTarget) {
            document.addEventListener('keydown', handleKeyDown);
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePopoverTarget]);

    // Normalize source columns from definitions or props
    const rawCols = definitions?.sourceFields || definitions?.sourceColumns || props.sourceColumns || [];
    const sourceColumnsList = rawCols.map(col => {
        const colName = typeof col === 'string' ? col : (col.name || col.COLUMN_NAME || '');
        const desc = typeof col === 'object' ? (col.description || col.label || '') : '';
        const colType = typeof col === 'object' ? (col.type || 'string') : 'string';
        const isPk = typeof col === 'object' ? (col.isPrimaryKey || false) : false;
        const colLabel = desc && desc !== colName ? `${colName} (${desc})` : colName;
        return { name: colName, label: colLabel, description: desc, type: colType, isPrimaryKey: isPk };
    }).filter(c => c.name);

    // Target fields lookup for types and requirements
    const targetFieldsLookup = new Map();
    (definitions?.targetFields || []).forEach(tf => {
        targetFieldsLookup.set(tf.name.toLowerCase(), tf);
    });

    // Helper to extract live QB sample values
    const getQbSampleValue = (fieldPath) => {
        if (!qbSampleRecords || qbSampleRecords.length === 0) return null;
        const rec = qbSampleRecords[activeQbSampleIndex];
        if (!rec) return null;
        const parts = fieldPath.split('.');
        let val = rec;
        for (const p of parts) {
            if (val == null) break;
            val = val[p];
        }
        return val;
    };

    // Helper to extract live PBS sample values
    const getPbsSampleValue = (fieldNames, template, delimiter) => {
        if (!pbsSampleRecords || pbsSampleRecords.length === 0) return null;
        const rec = pbsSampleRecords[activeSampleIndex];
        if (!rec) return null;
        if (template) {
            let str = template;
            fieldNames.forEach(f => {
                const regex = new RegExp(`\\{${f}\\}`, 'g');
                str = str.replace(regex, rec[f] !== undefined ? String(rec[f]).trim() : '');
            });
            return str;
        }
        const delim = delimiter !== undefined ? delimiter : ' - ';
        const vals = fieldNames
            .map(f => (rec[f] !== undefined && rec[f] !== null) ? String(rec[f]).trim() : '')
            .filter(v => v.length > 0);
        return vals.join(delim);
    };

    // Filter available columns for the active popover
    const filteredPopoverColumns = sourceColumnsList.filter(col => {
        if (!popoverSearch.trim()) return true;
        const q = popoverSearch.toLowerCase().trim();
        return col.name.toLowerCase().includes(q) || col.label.toLowerCase().includes(q) || col.description.toLowerCase().includes(q);
    });

    return (
        <div className="flex-1 overflow-y-auto flex flex-col bg-slate-900">
            {/* Live Sample Navigators - Shown in Pipeline Mode */}
            {!isStandardMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-950/70 border-b border-slate-800 shrink-0 text-xs">
                    {/* LEFT: Source ERP Sample Navigator */}
                    <div className="bg-slate-900 border border-emerald-900/50 rounded-xl p-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-emerald-400">
                            <span className="material-symbols-outlined text-[16px]">database</span>
                            <span>Source Record ({activeTableName || 'ERP'})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(0)}
                                disabled={activeSampleIndex === 0 || !pbsSampleRecords || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&laquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(Math.max(0, activeSampleIndex - 1))}
                                disabled={activeSampleIndex === 0 || !pbsSampleRecords || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&lsaquo;</button>
                            <span className="text-slate-400 mx-1 font-mono text-[11px]">
                                {pbsSampleRecords?.length > 0 ? activeSampleIndex + 1 : 0} / {pbsSampleRecords?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(Math.min((pbsSampleRecords?.length || 1) - 1, activeSampleIndex + 1))}
                                disabled={!pbsSampleRecords || activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&rsaquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex((pbsSampleRecords?.length || 1) - 1)}
                                disabled={!pbsSampleRecords || activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&raquo;</button>
                        </div>
                    </div>

                    {/* RIGHT: Target QuickBooks Sample Navigator */}
                    <div className="bg-slate-900 border border-purple-900/50 rounded-xl p-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-purple-300">
                            <span className="material-symbols-outlined text-[16px]">cloud</span>
                            <span>Target Record ({selectedQBEntity})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(0)}
                                disabled={activeQbSampleIndex === 0 || !qbSampleRecords || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&laquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(Math.max(0, activeQbSampleIndex - 1))}
                                disabled={activeQbSampleIndex === 0 || !qbSampleRecords || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&lsaquo;</button>
                            <span className="text-slate-400 mx-1 font-mono text-[11px]">
                                {qbSampleRecords?.length > 0 ? activeQbSampleIndex + 1 : 0} / {qbSampleRecords?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(Math.min((qbSampleRecords?.length || 1) - 1, activeQbSampleIndex + 1))}
                                disabled={!qbSampleRecords || activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&rsaquo;</button>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex((qbSampleRecords?.length || 1) - 1)}
                                disabled={!qbSampleRecords || activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&raquo;</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3-Column Left-to-Right Mapping Canvas Table */}
            <div className="flex-1 overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="sticky top-0 bg-slate-900 border-b border-slate-750 z-20 shadow-md">
                        <tr>
                            {/* Column 1: Source Builder (~44%) */}
                            <th className="px-4 py-3 font-semibold text-slate-300 w-[44%]">
                                <div className="flex items-center gap-1.5 text-emerald-400">
                                    <span className="material-symbols-outlined text-[16px]">input</span>
                                    <span>Source Builder (Input / ERP Data)</span>
                                </div>
                            </th>

                            {/* Column 2: Transform & Operator (~14%) */}
                            <th className="px-3 py-3 font-semibold text-slate-300 w-[14%] text-center border-l border-slate-800">
                                <div className="flex items-center justify-center gap-1 text-slate-400">
                                    <span className="material-symbols-outlined text-[16px]">transform</span>
                                    <span>Operator</span>
                                </div>
                            </th>

                            {/* Column 3: Target Destination (~42%) */}
                            <th className="px-4 py-3 font-semibold text-slate-300 w-[42%] border-l border-slate-800">
                                <div className="flex items-center gap-1.5 text-purple-300">
                                    <span className="material-symbols-outlined text-[16px]">output</span>
                                    <span>Target Destination (Endpoint / Schema)</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                        {filteredMappings.length === 0 ? (
                            <tr>
                                <td colSpan="3" className="px-4 py-12 text-center text-slate-400">
                                    <span className="material-symbols-outlined text-3xl mb-1 text-slate-500">search_off</span>
                                    <p className="text-sm font-semibold">No fields match your search filter</p>
                                    <p className="text-xs text-slate-500 mt-1">Try clearing the search query to see all mappings.</p>
                                </td>
                            </tr>
                        ) : null}

                        {filteredMappings.map((mapping, idx) => {
                            const targetMeta = targetFieldsLookup.get((mapping.target || '').toLowerCase());
                            const isReq = targetMeta?.required || false;
                            const isCustom = !targetMeta;
                            const targetType = targetMeta?.type || 'string';
                            const qbVal = isStandardMode ? null : getQbSampleValue(mapping.target);
                            const pbsVal = isStandardMode ? null : getPbsSampleValue(mapping.sources || [], mapping.template, mapping.delimiter);
                            const isPopoverOpen = activePopoverTarget === mapping.target;
                            const assignedSources = Array.isArray(mapping.sources) ? mapping.sources : (mapping.source ? [mapping.source] : []);

                            return (
                                <tr key={`${mapping.target}-${idx}`} className="hover:bg-slate-850/50 group transition-colors">
                                    {/* ── 1. LEFT COLUMN: SOURCE BUILDER (INPUT) ── */}
                                    <td className="px-4 py-3 align-top relative">
                                        <div className="flex flex-wrap items-center gap-1.5 min-h-[28px]">
                                            {/* Assigned Column Tokens */}
                                            {assignedSources.map((src, sIdx) => (
                                                <span
                                                    key={sIdx}
                                                    className="inline-flex items-center gap-1 bg-emerald-950/80 border border-emerald-700/70 text-emerald-300 px-2 py-0.5 rounded-md font-mono text-[11px] shadow-sm"
                                                    title={`Assigned ERP source column #${sIdx + 1}`}
                                                >
                                                    <span>{src}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const ns = assignedSources.filter((_, i) => i !== sIdx);
                                                            handleCustomMappingChange(mapping.target, 'sources', ns);
                                                        }}
                                                        className="text-emerald-400/60 hover:text-rose-400 font-bold transition-colors ml-0.5 focus:outline-none"
                                                        title="Remove column from this mapping"
                                                    >&times;</button>
                                                </span>
                                            ))}

                                            {/* Add Field Button & Popover Trigger */}
                                            <div className="relative inline-block">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (isPopoverOpen) {
                                                            setActivePopoverTarget(null);
                                                            setPopoverSearch('');
                                                        } else {
                                                            setActivePopoverTarget(mapping.target);
                                                            setPopoverSearch('');
                                                        }
                                                    }}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white border border-slate-700 text-[11px] font-medium transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">add</span>
                                                    <span>Add Field</span>
                                                </button>

                                                {/* Searchable Column Selection Popover */}
                                                {isPopoverOpen && (
                                                    <div
                                                        ref={popoverRef}
                                                        className="absolute left-0 top-full mt-1 w-72 bg-slate-950 border border-slate-700 rounded-xl shadow-2xl p-2 z-50 space-y-1.5 animate-in fade-in zoom-in-95 duration-100"
                                                    >
                                                        <div className="relative">
                                                            <span className="material-symbols-outlined text-slate-400 text-[14px] absolute left-2 top-2">search</span>
                                                            <input
                                                                type="text"
                                                                autoFocus
                                                                value={popoverSearch}
                                                                onChange={e => setPopoverSearch(e.target.value)}
                                                                placeholder="Search column or label..."
                                                                className="w-full bg-slate-900 border border-slate-700 rounded-md pl-7 pr-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500"
                                                            />
                                                        </div>
                                                        <div className="max-h-48 overflow-y-auto divide-y divide-slate-800/40 text-[11px]">
                                                            {filteredPopoverColumns.length === 0 ? (
                                                                <div className="p-2 text-center text-slate-500 italic">
                                                                    No columns found
                                                                </div>
                                                            ) : (
                                                                filteredPopoverColumns.map(col => (
                                                                    <button
                                                                        key={col.name}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            if (!assignedSources.includes(col.name)) {
                                                                                const ns = [...assignedSources, col.name];
                                                                                handleCustomMappingChange(mapping.target, 'sources', ns);
                                                                            }
                                                                            setActivePopoverTarget(null);
                                                                            setPopoverSearch('');
                                                                        }}
                                                                        className="w-full text-left px-2 py-1.5 hover:bg-slate-800/80 rounded flex flex-col gap-0.5 transition-colors"
                                                                    >
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="font-mono font-bold text-emerald-400">{col.name}</span>
                                                                            {col.isPrimaryKey && (
                                                                                <span className="text-[9px] px-1 py-0.2 bg-amber-950 text-amber-400 rounded border border-amber-800 font-mono">PK</span>
                                                                            )}
                                                                        </div>
                                                                        {col.description && (
                                                                            <span className="text-[10px] text-slate-400 truncate">{col.description}</span>
                                                                        )}
                                                                    </button>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Live Source Sample Snippet (Pipeline Mode) */}
                                        {!isStandardMode && pbsVal && (
                                            <div className="mt-2 text-[10px] text-emerald-300 font-mono bg-slate-950/70 px-2 py-1 rounded border border-slate-800/80 overflow-x-auto whitespace-nowrap">
                                                <span className="text-slate-500 font-sans mr-1.5">Live ERP Value:</span>
                                                <span>{pbsVal}</span>
                                            </div>
                                        )}
                                    </td>

                                    {/* ── 2. CENTER COLUMN: TRANSFORMATION & OPERATOR ── */}
                                    <td className="px-3 py-3 align-top text-center border-l border-slate-800/60">
                                        <div className="flex flex-col items-center justify-center gap-1.5 pt-0.5">
                                            {/* Delimiter selector if multi-source tokens assigned */}
                                            {assignedSources.length > 1 && (
                                                <div className="flex items-center gap-1" title="Separator delimiter for multiple source fields">
                                                    <span className="text-[10px] text-slate-500 uppercase font-bold">Sep:</span>
                                                    <select
                                                        value={mapping.delimiter !== undefined ? mapping.delimiter : ' - '}
                                                        onChange={(e) => handleCustomMappingChange(mapping.target, 'delimiter', e.target.value)}
                                                        className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-amber-300 font-mono text-[10px] focus:outline-none focus:border-indigo-500"
                                                    >
                                                        <option value=" - ">- (dash)</option>
                                                        <option value=" "> (space)</option>
                                                        <option value=", ">, (comma)</option>
                                                        <option value="/">/ (slash)</option>
                                                        <option value="">(none)</option>
                                                    </select>
                                                </div>
                                            )}

                                            {/* Directional Flow Link Arrow */}
                                            <div className="flex items-center gap-1 text-slate-500 group-hover:text-indigo-400 transition-colors">
                                                <span className="material-symbols-outlined text-[18px]">trending_flat</span>
                                            </div>
                                        </div>
                                    </td>

                                    {/* ── 3. RIGHT COLUMN: TARGET DESTINATION (ENDPOINT) ── */}
                                    <td className="px-4 py-3 align-top border-l border-slate-800/60">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="font-mono text-xs font-semibold text-slate-100">{mapping.target}</span>
                                                    {isReq && (
                                                        <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-950 text-amber-300 border border-amber-800" title="Required Target Field">
                                                            Required *
                                                        </span>
                                                    )}
                                                    {isCustom && (
                                                        <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-indigo-950 text-indigo-300 border border-indigo-800">
                                                            CUSTOM
                                                        </span>
                                                    )}
                                                    <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-slate-800 text-slate-400">
                                                        {targetType}
                                                    </span>
                                                </div>
                                                {targetMeta?.description && (
                                                    <p className="text-[10px] text-slate-400 leading-tight">
                                                        {targetMeta.description}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Action Button: Clear or Delete Mapping */}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isCustom) {
                                                        handleRemoveCustomRow(mapping.target);
                                                    } else {
                                                        handleCustomMappingChange(mapping.target, 'sources', []);
                                                    }
                                                }}
                                                className="text-slate-500 hover:text-rose-400 transition-colors p-1 rounded hover:bg-slate-800 shrink-0"
                                                title={isCustom ? "Delete custom mapping row" : "Clear mapped source fields"}
                                            >
                                                <span className="material-symbols-outlined text-[16px]">
                                                    {isCustom ? 'delete' : 'backspace'}
                                                </span>
                                            </button>
                                        </div>

                                        {/* Live Target Sample Snippet (Pipeline Mode) */}
                                        {!isStandardMode && qbVal !== null && qbVal !== undefined && (
                                            <div className="mt-2 text-[10px] text-purple-300 font-mono bg-slate-950/70 px-2 py-1 rounded border border-slate-800/80 overflow-x-auto whitespace-nowrap">
                                                <span className="text-slate-500 font-sans mr-1.5">Live Target Value:</span>
                                                <span>{typeof qbVal === 'object' ? JSON.stringify(qbVal) : String(qbVal)}</span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}

                        {/* ── DEDICATED BOTTOM ROW: CUSTOM ENDPOINT CREATION ── */}
                        <tr className="bg-slate-900/90 border-t-2 border-indigo-900/50">
                            {/* Left: Source Column Selector */}
                            <td className="px-4 py-3 align-middle">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        Map Source Column:
                                    </span>
                                    <select
                                        value={newSource}
                                        onChange={e => setNewSource(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-indigo-500 focus:outline-none font-mono"
                                    >
                                        <option value="">-- Select ERP Source Column --</option>
                                        {sourceColumnsList.map(c => (
                                            <option key={c.name} value={c.name}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </td>

                            {/* Center: Operator Arrow */}
                            <td className="px-3 py-3 align-middle text-center border-l border-slate-800/60">
                                <span className="material-symbols-outlined text-indigo-400 text-[18px]">trending_flat</span>
                            </td>

                            {/* Right: Custom Target Endpoint & Add Button */}
                            <td className="px-4 py-3 align-middle border-l border-slate-800/60">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        To Custom Target Endpoint:
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            placeholder="e.g. CustomField.RegionCode"
                                            value={newTarget}
                                            onChange={e => setNewTarget(e.target.value)}
                                            className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddCustomMapping}
                                            disabled={!newTarget.trim() || !newSource.trim()}
                                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 text-white rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors shrink-0 shadow-sm"
                                        >
                                            <span className="material-symbols-outlined text-[15px]">add_circle</span>
                                            <span>Add Mapping</span>
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
